import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV, PROJECTS, ASSETS } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [projOpen, setProjOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setProjOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <header
      data-testid="site-navbar"
      className={
        "sticky top-0 z-50 transition-all duration-300 " +
        (scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70"
          : "bg-white/40 backdrop-blur-md border-b border-transparent")
      }
    >
      <div className="container-x flex h-[74px] items-center justify-between gap-6">
        <Link to="/" data-testid="navbar-logo" className="flex items-center shrink-0" aria-label="Făt-Frumos acasă">
          <img src={ASSETS.logo} alt="Făt-Frumos" className="h-9 w-auto md:h-10" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace(/[^a-z]/gi, "") || "brands"}`}
              className={({ isActive }) =>
                "link-underline text-[15px] font-semibold transition-colors " +
                (isActive && !item.to.includes("#") ? "text-brand" : "text-ink hover:text-brand")
              }
            >
              {item.label}
            </NavLink>
          ))}

          <div
            className="relative"
            onMouseEnter={() => setProjOpen(true)}
            onMouseLeave={() => setProjOpen(false)}
          >
            <button
              data-testid="nav-projects-toggle"
              onClick={() => setProjOpen((v) => !v)}
              className="flex items-center gap-1 text-[15px] font-semibold text-ink hover:text-brand transition-colors"
            >
              Proiectele noastre
              <ChevronDown className={"h-4 w-4 transition-transform " + (projOpen ? "rotate-180" : "")} />
            </button>
            {projOpen && (
              <div className="absolute right-0 top-full pt-3">
                <div className="w-72 card p-2" data-testid="nav-projects-menu">
                  {PROJECTS.map((p) => (
                    <a
                      key={p.href}
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-semibold text-ink hover:bg-slate-50 transition-colors"
                    >
                      {p.label}
                      <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-brand transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="navbar-cta" className="btn-primary text-[15px]">
            Contactează-ne
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/70 border border-slate-200 text-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Meniu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container-x py-5 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="rounded-2xl px-4 py-3 text-base font-semibold text-ink hover:bg-slate-50"
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-2 px-4 pt-3 pb-1 text-xs font-extrabold uppercase tracking-[0.2em] text-slate-400">
              Proiectele noastre
            </div>
            {PROJECTS.map((p) => (
              <a
                key={p.href}
                href={p.href}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-2xl px-4 py-3 text-base font-semibold text-ink hover:bg-slate-50"
              >
                {p.label}
                <ArrowUpRight className="h-4 w-4 text-slate-400" />
              </a>
            ))}
            <Link to="/contact" className="btn-primary mt-4 w-full">
              Contactează-ne
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
